import { IConfigTicket, Reduce } from '@augloop/schemas/dist/src/client/schemas';
import { IBatchConfig } from './iclientruntime';

/**
 * @deprecated V1 runtime type that should no longer be used
 */
export enum LambdaSource {
    Unknown = 0,
    Local = 1,
    Custom = 2,
    Remote = 3,
    BatchedRemote = 4
}

/**
 * Definition of a lambda that transforms a given input schema into an output schema.
 * The schemas must be registered via registerSchemas().
 *
 * @deprecated V1 runtime type that should no longer be used
 */
export interface ILambda {
    /**
     * Lambda name (for telemetry and debugging purposes), max 40 characters.
     */
    name: string;

    /**
     * The name of the input schema (the 'input' parameter to your function will conform to this schema).
     */
    inputSchema: string;

    /**
     * The name of the output schema (the output passed to 'done' in your function will conform to this schema).
     */
    outputSchema: string;

    /**
     * The location of the Lambda (Server, Native or Javascript)
     */
    source: LambdaSource;

    /**
     * The lambda-specific configuration object. Can be updated (will take effect in subsequent invocations
     * of the lambda function).
     */
    config?: any;

    /**
     * Whether this lambda is whitelisted to run even when users opt out of running intelligent services.
     * The schema used for this lambda and data sent over the wire must be reviewed and approved by the privacy team.
     */
    canRunInPrivateMode?: boolean;
}

/**
 * Contains context information for lambda execution
 *
 * @deprecated V1 runtime type that should no longer be used
 */
export interface ILambdaContext {
    /**
     * A lookup table that contains information useful for workflow. May help workflow
     * to determine not to make a call to service.
     */
    lookupTable?: any;

    /**
     * The docId for the workflow execution.
     */
    docId?: string;

    /**
     * Configuration for the auth token to obtain before executing the lambda.
     */
    authTickets?: IConfigTicket[];
}

/**
 * @deprecated V1 runtime type that should no longer be used
 */
export interface IRemoteLambda extends ILambda {
    /**
     * Configuration for the auth token to obtain before executing the lambda.
     */
    readonly authTickets?: IConfigTicket[];
}

/**
 * Definition of a batched remote lambda, which accumulates inputs (grouped by a lambda-defined key)
 * until a timeout expires or a maximum size limit is reached, then multiplexes those inputs into
 * a single remote lambda request, executes it, and demultiplexes the remote lambda output back into
 * individual outputs corresponding to the original inputs.
 *
 * @deprecated V1 runtime type that should no longer be used
 */
export interface IBatchedRemoteLambda extends IRemoteLambda {
    /**
     * Configuration for batching data sent to the AL service.
     */
    batchOptions: IBatchConfig<any>;

    /**
     * The name of the schema that will be sent to the Augmentation Loop service.
     */
    remoteInputSchema: string;

    /**
     * The name of the schema that will be received from the Augmentation Loop service.
     */
    remoteOutputSchema: string;
}

/**
 * @deprecated V1 runtime type that should no longer be used
 */
export interface ILocalLambda extends ILambda {}

/**
 * @deprecated V1 runtime type that should no longer be used
 */
export interface ICustomLambda extends ILambda {
    /**
     * Executes the logic of this lambda.
     *
     * @param input The input object, which conforms to inputSchema.
     *
     * @param config A lambda-specific configuration object. Multiple invocations of the lambda MAY receive the
     * same config object, giving an opportunity to cache data between calls, but persistence is not guaranteed
     * and you cannot rely on this for functional correctness.
     *
     * @param context Contains context information for lambda execution.
     *
     * @param done Callback to indicate that the lambda has finished processing. Pass in the output object
     * (which must conform to outputSchema), or null to indicate that the lambda produces no output.
     *
     * @param finishWorkflow  Callback to indicate that the Workflow has finished processing. Pass in the output object
     * (which must conform to outputSchema of the workflow ), or null to indicate that the workflow produces no
     * output.
     */
    func: (input: any, config: any, context: ILambdaContext, done: (output: any) => void, finishWorkflow: (output: any, outputSchema: string) => void) => void;
}

/**
 * @deprecated V1 runtime type that should no longer be used
 */
export interface IReduceLambda extends ILambda {
    /**
     * Executes the logic of this reduce lambda.
     *
     * @param input The input containing values conforming to inputSchema.
     *
     * @param config A lambda-specific configuration object. Multiple invocations of the lambda MAY receive the
     * same config object, giving an opportunity to cache data between calls, but persistence is not guaranteed
     * and you cannot rely on this for functional correctness.
     *
     * @param context Contains context information for remote lambda execution.
     *
     * @param done Callback to indicate that the lambda has finished processing. Pass in the output object
     * (which must conform to outputSchema), or null to indicate that the lambda produces no output.
     *
     * @param finishWorkflow Callback to indicate that the Workflow has finished processing. Pass in the output object
     * (which must conform to outputSchema of the workflow ), or null to indicate that the workflow produces no
     * output.
     */
    func: (input: Reduce.IInput, config: any, context: ILambdaContext, done: (output: any) => void, finishWorkflow: (output: any, outputSchema: string) => void) => void;
}

/**
 * Settings for throttling of workflows
 * I.e. For acronyms we don't want to run workflows for each tile change event, rather we want to run it once every n seconds.
 *
 * @deprecated V1 runtime type that should no longer be used
 */
export interface IThrottleSettings {
    /**
     * Whether workflow should be throttled
     */
    shouldBeThrottled: boolean;

    /**
     * The period in miliseconds for which we pause specific workflow's execution.
     */
    throttlingInterval?: number;
}

/**
 * Represents the cache grouping key for a reduce workflow input message.
 *
 * @deprecated V1 runtime type that should no longer be used
 */
export interface IReduceWorkflowGroupingKey {
    /**
     * Primary grouping key in the cache, all messages under this primary key get processed together when the
     * reduce workflow is called.
     */
    primary: string;

    /**
     * An optional secondary key, when provided only one message is stored for each secondary key within the primary
     * key group. The newest message always wins.
     */
    secondary?: string;
}

/**
 * Data for client to execute workflow
 *
 * @deprecated V1 runtime type that should no longer be used
 */
export interface IWorkflowRegistrationOptions {
    /**
     * Whether the workflow should produce a null result if lambda returns null.
     */
    readonly canProduceNullResult?: boolean;

    /**
     * Whether the workflow should send results to the host or not.
     */
    readonly shouldSendResultsToHost?: boolean;

    /**
     * Whether the workflow is enabled by default or requires an explicit flight to enable it.
     * Limited to Win32/Mac. Should be true when a feature has fully rolled out and shipped.
     */
    readonly enabledByDefault?: boolean;

    /**
     * Whether the workflow should be throttled - some workflows we want to trigger only once every n seconds. Effectively, this
     * means we don't trigger it on each tiling event, rather we delay the execution for n seconds.
     */
    readonly throttleSettings?: IThrottleSettings;

    /**
     * License features to check before registering this workflow. If any of these features are not enabled under the current
     * user's license the workflow will not be registered.
     */
    readonly licenseFeatures?: number[];

    /**
     * Lambda for retrieving the workflow's lookup table.
     */
    readonly lookupTableLambda?: IRemoteLambda;

    /**
     * Function to extract the docId from the workflow input and set on the lambda context.
     */
    readonly docIdExtractor?: (input: any) => string;

    /**
     * Whether the workflow should be triggered by refresh tiles
     */
    readonly triggerOnRefresh?: boolean;

    /**
     * Whether to defer reduce executions until upstream workflows have gone idle
     */
    readonly deferReduceUntilIdle?: boolean;

    /**
     * Maximum time to defer reduction execution even if upstream workflows are not yet idle
     */
    readonly deferReduceUntilIdleMaxDelayMs?: number;

    /**
     * Whether to submit a canary text tile on first execution of the workflow
     */
    readonly useCanaryTextTile?: boolean;
}
